// Story 7.5 (ADO-909) — favourites state in a Zustand store (AC1/AC2).
//
// project-context React rules (ADR-004): Zustand for all client-side global
// state. The SERVER is the source of truth (supersedes the prototype's
// localStorage-only favourites-context, ux-design-specification-addendum.md:227):
// every mutation returns the full post-state list, which replaces the store.
//
// Two projections of the same data:
//   • `favouriteSkus` — a Set for the per-SKU heart toggle on every ProductCard
//     (cards subscribe with `s.favouriteSkus.has(sku)`, the same shape by which
//     they read `useAvailabilityStore`).
//   • `favourites` — the full product list for the /favourites screen.
//
// Add/remove are OPTIMISTIC so the heart flips immediately (AC1 "favourited
// immediately"); on the server response the store reconciles to truth, and on a
// failure it rolls back the optimistic change (the heart never lies about a
// write that did not land).
//
// Enabler E7-C (ADO-2274), D-1: every call names the selected ordering entry when
// there is one, so each favourite is summarised from its row in that entry's range.
// The boot hydration runs before an entry exists and sends none; an entry change
// re-hydrates (`startCatalogueEntryResets`).
//
// LATEST OPERATION WINS. Reads and writes run one at a time, in the order they were
// asked for, so each answer is the server's list after every earlier operation. An
// answer is applied only when its operation is still the latest one asked for in the
// same session: a later read or write will bring a newer list, so an earlier answer
// (summarised under a previous entry, or from before a write) never overwrites it. A
// sign-out (`reset`) ends the session: operations still queued do not run, and an answer
// that lands afterwards is dropped. A write's optimistic heart and its rollback are per
// SKU and apply in the session whatever comes after, so the heart never lies.

import { create } from 'zustand';
import {
  addFavourite,
  listFavourites,
  removeFavourite,
  type FavouriteProduct,
} from '../api/favourites.client';
import { resolveCatalogueEntryKey } from '../ordering/catalogueEntry';

/** Bumped by `reset()` (sign-out): operations of an earlier session neither run nor land. */
let session = 0;
/** The ticket of the latest operation asked for; an answer lands only on that ticket. */
let latestTicket = 0;
/** The tail of the one-at-a-time queue. */
let queue: Promise<void> = Promise.resolve();
/**
 * Whether an answer of this session was dropped for a later operation since one last landed. If
 * that later operation then fails, nothing has brought the list, so it is read again.
 */
let answerDropped = false;

interface Operation {
  readonly session: number;
  readonly ticket: number;
}

/** In the operation's session, and nothing newer asked for since: its answer is the truth. */
function isLatest(operation: Operation): boolean {
  return operation.session === session && operation.ticket === latestTicket;
}

function inSession(operation: Operation): boolean {
  return operation.session === session;
}

/**
 * Queue `run` behind every earlier operation. It is skipped when the session has ended by its
 * turn. `run` handles its own failures, so the queue never stalls.
 */
function enqueue(run: (operation: Operation) => Promise<void>): Promise<void> {
  const operation: Operation = { session, ticket: ++latestTicket };
  const next = queue.then(() => (inSession(operation) ? run(operation) : undefined));
  queue = next.catch(() => undefined);
  return next;
}

interface FavouritesStoreState {
  /** Full favourited products (newest first) — drives the /favourites screen. */
  favourites: readonly FavouriteProduct[];
  /** Favourited SKU codes — drives the per-card heart toggle. */
  favouriteSkus: ReadonlySet<string>;
  /** True once the initial list has loaded (so hearts reflect real state). */
  hydrated: boolean;
  /** True when the last `hydrate` failed — lets the screen offer a retry rather
   *  than pinning the user on an infinite spinner (Story 7.5 review). */
  error: boolean;
  /** SKUs with an add/remove in flight — a per-SKU mutex so a double-click
   *  (add↔remove) cannot race two full-post-state reconciles to a wrong final
   *  state (Story 7.5 review). */
  pending: ReadonlySet<string>;
  /** The last add/remove that FAILED, keyed by SKU + the server's user-facing
   *  message (Story 7.5 review). Without this the optimistic heart just rolls
   *  back silently — at the per-user cap it flips on and straight back off with
   *  no explanation, on every retry. The card reads `mutationError.sku` to show
   *  the reason (e.g. the 500-favourite cap 400) beside the bouncing heart.
   *  Cleared when a new mutation for that SKU starts or succeeds. */
  mutationError: { readonly sku: string; readonly message: string } | null;
  /** Load the caller's favourites at boot / on entering a favourites-aware screen. */
  hydrate: () => Promise<void>;
  /** Optimistically favourite a SKU, then reconcile with the server (AC1/AC3). */
  add: (skuCode: string) => Promise<void>;
  /** Optimistically unfavourite a SKU, then reconcile with the server (AC2). */
  remove: (skuCode: string) => Promise<void>;
  /** Test/session hygiene — drop all state. */
  reset: () => void;
}

function skuSet(list: readonly FavouriteProduct[]): ReadonlySet<string> {
  return new Set(list.map((f) => f.sku));
}

function withSku(set: ReadonlySet<string>, sku: string): ReadonlySet<string> {
  const next = new Set(set);
  next.add(sku);
  return next;
}

function withoutSku(set: ReadonlySet<string>, sku: string): ReadonlySet<string> {
  const next = new Set(set);
  next.delete(sku);
  return next;
}

/** The client throws an Error whose message is the server's user-facing text
 *  (favourites.client surfaces the Nest 400 body); fall back to a generic line
 *  if some non-Error was thrown. */
function messageOf(err: unknown): string {
  return err instanceof Error && err.message.length > 0
    ? err.message
    : 'Something went wrong. Please try again.';
}

export const useFavouritesStore = create<FavouritesStoreState>((set, get) => {
  /** A write's whole-list answer: applied when its operation is the latest, else dropped. */
  const landOrDrop = (operation: Operation, list: readonly FavouriteProduct[]): void => {
    if (!isLatest(operation)) {
      answerDropped = true;
      return;
    }
    answerDropped = false;
    set({ favourites: list, favouriteSkus: skuSet(list), hydrated: true, error: false });
  };

  /** The latest operation failed after an earlier answer was dropped: read the list, in turn. */
  const readAgainIfNothingLanded = (operation: Operation): void => {
    if (isLatest(operation) && answerDropped) void get().hydrate();
  };

  return {
    favourites: [],
    favouriteSkus: new Set<string>(),
    hydrated: false,
    error: false,
    pending: new Set<string>(),
    mutationError: null,

    hydrate() {
      set({ error: false });
      return enqueue(async (operation) => {
        try {
          // The entry as of this read's turn, not of the call: an entry change since queues
          // its own read, which is then the latest.
          const list = await listFavourites(resolveCatalogueEntryKey());
          if (!isLatest(operation)) {
            if (inSession(operation)) answerDropped = true;
            return;
          }
          answerDropped = false;
          set({ favourites: list, favouriteSkus: skuSet(list), hydrated: true, error: false });
        } catch {
          if (!isLatest(operation)) return;
          // A hydration failure must not blank hearts or crash a listing — leave the
          // current state (empty on first load) but FLAG the error so the screen can
          // surface a retry affordance instead of an infinite "Loading…" spinner.
          set({ error: true });
        }
      });
    },

    add(skuCode) {
      const { favouriteSkus, pending, mutationError } = get();
      // Already favourited (AC3 no-op) OR a mutation for this SKU is already in
      // flight — ignore the second click so two reconciles can't race.
      if (favouriteSkus.has(skuCode) || pending.has(skuCode)) return Promise.resolve();
      // Optimistic: fill the heart at once. We don't have the product detail here
      // (favouriting from a browse card), so only the SKU set changes until the
      // server returns the composed list. Clear any stale error for this SKU as we
      // retry so the message doesn't linger past a fresh attempt.
      set({
        favouriteSkus: withSku(favouriteSkus, skuCode),
        pending: withSku(pending, skuCode),
        mutationError: mutationError?.sku === skuCode ? null : mutationError,
      });
      return enqueue(async (operation) => {
        try {
          const list = await addFavourite(skuCode, resolveCatalogueEntryKey());
          if (!inSession(operation)) return;
          set({ pending: withoutSku(get().pending, skuCode) });
          // The whole list after this write; a later operation's list supersedes it.
          landOrDrop(operation, list);
        } catch (err) {
          if (!inSession(operation)) return;
          // Roll back the optimistic heart against CURRENT state (not a stale
          // snapshot) so a concurrent mutation on another SKU is preserved, and
          // EXPOSE why (e.g. the per-user cap 400) so the card can tell the user
          // rather than the heart bouncing on/off with no explanation.
          set({
            favouriteSkus: withoutSku(get().favouriteSkus, skuCode),
            pending: withoutSku(get().pending, skuCode),
            mutationError: { sku: skuCode, message: messageOf(err) },
          });
          readAgainIfNothingLanded(operation);
        }
      });
    },

    remove(skuCode) {
      const { favouriteSkus, favourites, pending, mutationError } = get();
      if (!favouriteSkus.has(skuCode) || pending.has(skuCode)) return Promise.resolve(); // no-op (AC2) or in-flight
      const removed = favourites.find((f) => f.sku === skuCode);
      // Optimistic: unfill the heart AND drop it from the visible list. Clear any
      // stale error for this SKU as we retry.
      set({
        favouriteSkus: withoutSku(favouriteSkus, skuCode),
        favourites: favourites.filter((f) => f.sku !== skuCode),
        pending: withSku(pending, skuCode),
        mutationError: mutationError?.sku === skuCode ? null : mutationError,
      });
      return enqueue(async (operation) => {
        try {
          const list = await removeFavourite(skuCode, resolveCatalogueEntryKey());
          if (!inSession(operation)) return;
          set({ pending: withoutSku(get().pending, skuCode) });
          landOrDrop(operation, list);
        } catch (err) {
          if (!inSession(operation)) return;
          // Roll back against CURRENT state — re-add the SKU (and its product, if we
          // captured it) so a concurrent mutation on another SKU is not clobbered by
          // a stale full-list snapshot — and EXPOSE why the removal did not land.
          const current = get();
          const restoredList =
            removed && !current.favourites.some((f) => f.sku === skuCode)
              ? [removed, ...current.favourites]
              : current.favourites;
          set({
            favouriteSkus: withSku(current.favouriteSkus, skuCode),
            favourites: restoredList,
            pending: withoutSku(current.pending, skuCode),
            mutationError: { sku: skuCode, message: messageOf(err) },
          });
          readAgainIfNothingLanded(operation);
        }
      });
    },

    reset() {
      // Ends the session: queued operations do not run, and answers still in flight are dropped.
      // The next session starts its own queue, so it never waits on a request of this one.
      session += 1;
      queue = Promise.resolve();
      answerDropped = false;
      set({
        favourites: [],
        favouriteSkus: new Set<string>(),
        hydrated: false,
        error: false,
        pending: new Set<string>(),
        mutationError: null,
      });
    },
  };
});
